/* eslint-disable consistent-return */
import { Meteor } from 'meteor/meteor';
import { check, Match } from 'meteor/check';
import SimpleSchema from 'simpl-schema';
import { Accounts } from 'meteor/accounts-base';
import { Roles } from 'meteor/alanning:roles';

import rateLimit from '../../../modules/rate-limit';
import handleMethodException from '../../../modules/handle-method-exception';
import sendUpgradeRequestEmail from './send-upgrade-request-email';
import sendWelcomeEmail from './send-welcome-email';

import deleteAccount from './delete-account';
import editProfile from './edit-profile';

const newUserSchema = new SimpleSchema({
  profile: {
    type: Object
  },
  'profile.name': {
    type: Object
  },
  'profile.name.first': {
    type: String
  },
  'profile.name.last': {
    type: String
  },
  'profile.defaultBaseMap': {
    type: String,
    optional: true
  },
  'profile.featureSelectedColor': {
    type: String,
    optional: true
  },
  'profile.featureSelectedWeight': {
    type: Number,
    optional: true
  },
  'profile.defaultMapCenter': {
    type: Object,
    optional: true
  },
  email: {
    type: String,
    regEx: SimpleSchema.RegEx.Email
  },
  role: {
    type: String
  },
  password: {
    type: String,
    min: 6
  }
});

const editUserSchema = new SimpleSchema({
  profile: {
    type: Object
  },
  'profile.name': {
    type: Object
  },
  'profile.name.first': {
    type: String
  },
  'profile.name.last': {
    type: String
  },
  'profile.defaultBaseMap': {
    type: String,
    optional: true
  },
  'profile.featureSelectedColor': {
    type: String,
    optional: true
  },
  'profile.featureSelectedWeight': {
    type: Number,
    optional: true
  },
  'profile.defaultMapCenter': {
    type: Object,
    optional: true
  },
  email: {
    type: String,
    regEx: SimpleSchema.RegEx.Email
  },
  _id: String
});

Meteor.methods({
  'users.changeRole': function usersChangeRole(userId2Change, newRole, isInRole) {
    check(userId2Change, String);
    check(newRole, String);
    check(isInRole, Boolean);
    try {
      if (Roles.userIsInRole(this.userId, ['admin'])) {
        if (isInRole) {
          Roles.addUsersToRoles(userId2Change, newRole);
        } else {
          Roles.removeUsersFromRoles(userId2Change, newRole);
        }
      } else {
        throw new Meteor.Error(500, 'Ha! Nice try, slick.');
      }
    } catch (exception) {
      handleMethodException(exception);
    }
  },
  'users.sendVerificationEmail': function usersSendVerificationEmail() {
    return Accounts.sendVerificationEmail(this.userId);
  },
  'users.sendWelcomeEmail': async function userSendWelcomeEmail() {
    try {
      return await sendWelcomeEmail();
    } catch (exception) {
      handleMethodException(exception);
    }
  },
  'users.changeStorageLimit': function usersChangeStorageLimit(userId2Change, newStorage) {
    check(userId2Change, String);
    check(newStorage, Number);
    try {
      if (Roles.userIsInRole(this.userId, ['admin'])) {
        Meteor.users.update(userId2Change, { $set: { 'storageCuota.sizeLimit': newStorage } });
      } else {
        throw new Meteor.Error(500, 'Ha! Nice try, slick.');
      }
    } catch (exception) {
      handleMethodException(exception);
    }
  },
  'users.changeUsedStorage': function usersChangeUsedStorage(newStorage) {
    check(newStorage, Number);
    try {
      Meteor.users.update(this.userId, { $set: { 'storageCuota.usedStorage': newStorage } });
    } catch (exception) {
      handleMethodException(exception);
    }
  },

  'users.updateEsriAccessToken': function usersUpdateEsriAccessToken(newToken) {
    check(newToken, String);
    try {
      Meteor.users.update(this.userId, { $set: { 'services.esri.accessToken': newToken } });
    } catch (exception) {
      handleMethodException(exception);
    }
  },
  'users.sendUpgradeRequestEmail': function usersSendUpgradeRequest(upgradeForm) {
    check(upgradeForm, Object);
    try {
      sendUpgradeRequestEmail(Meteor.user(), upgradeForm);
    } catch (exception) {
      handleMethodException(exception);
    }
  },
  'users.setPassword': function usersSetPassword(userId) {
    try {
      return Accounts.setPassword(userId, 'password');
    } catch (exception) {
      handleMethodException(exception);
    }
  },
  'users.softDelete': function usersSoftDelete(userId) {
    check(userId, String);
    try {
      if (Roles.userIsInRole(this.userId, ['admin'])) {
        Meteor.users.update(userId, { $set: { deleted: new Date().toISOString() } });
      } else if (Roles.userIsInRole(userId, ['admin'])) {
        throw new Meteor.Error(500, 'No way Jose');
      } else {
        throw new Meteor.Error(500, 'Ha! Nice try, slick.');
      }
    } catch (exception) {
      handleMethodException(exception);
    }
  },
  'users.restore': function projectsRestore(userId) {
    check(userId, String);
    try {
      if (Roles.userIsInRole(this.userId, ['admin'])) {
        Meteor.users.update(userId, { $set: { deleted: 'no' } });
      } else {
        throw new Meteor.Error(500, 'Ha! Nice try, slick.');
      }
    } catch (exception) {
      handleMethodException(exception);
    }
  },
  'users.hardDelete': async function usersHardDelete(userId) {
    check(userId, Match.Maybe(String));
    if (userId && !Roles.userIsInRole(this.userId, 'admin')) {
      throw new Meteor.Error('403', 'Sorry, you need to be an administrator to do this.');
    }
    try {
      return await deleteAccount({ userId: userId || this.userId });
    } catch (exception) {
      handleMethodException(exception);
    }
  },
  'users.insert': function usersInsert(user) {
    try {
      newUserSchema.validate(user);
      return Accounts.createUser(user);
    } catch (exception) {
      handleMethodException(exception);
    }
  },
  'users.editProfile': async function usersEditProfile(profile) {
    check(profile, {
      emailAddress: String,
      profile: {
        name: {
          first: String,
          last: String
        },
        defaultBaseMap: Match.Maybe(String),
        featureSelectedColor: Match.Maybe(String),
        featureSelectedWeight: Match.Maybe(Number)
      }
    });
    try {
      return await editProfile({ userId: this.userId, profile });
    } catch (exception) {
      handleMethodException(exception);
    }
  },
  'users.update': async function usersUpdate(user) {
    try {
      editUserSchema.validate(user);
      const userProfile = {
        emailAddress: user.email,
        profile: {
          name: {
            first: user.profile.name.first,
            last: user.profile.name.last
          },
          defaultBaseMap: user.profile.defaultBaseMap,
          featureSelectedColor: user.profile.featureSelectedColor,
          featureSelectedWeight: user.profile.featureSelectedWeight
        }
      };
      if (!Roles.userIsInRole(this.userId, ['admin'])) {
        throw new Meteor.Error(500, 'Ha! Nice try, slick.');
      }
      try {
        return await editProfile({ userId: user._id, profile: userProfile });
      } catch (exception) {
        handleMethodException(exception);
      }
    } catch (exception) {
      handleMethodException(exception);
    }
  },
  'users.fetchSettings': function usersFetchSettings(options) {
    // eslint-disable-line
    check(options, Match.Maybe(Object));

    try {
      const user = Meteor.users.findOne(
        { _id: options.userId || this.userId },
        { fields: { settings: 1 } }
      );

      if (user && user.settings) {
        if (!options.gdpr && !options.isAdmin) return user.settings;
        if (options.gdpr && !options.isAdmin)
          return user.settings.filter(setting => setting.isGDPR === true);
        if (options.isAdmin)
          return user.settings.filter(
            setting => setting.isGDPR === false || typeof setting.isGDPR === 'undefined'
          );
      }

      return [];
    } catch (exception) {
      console.warn(exception);
      handleMethodException(exception);
    }
  },
  'users.checkIfGDPRComplete': function usersCheckIfGDPRComplete() {
    // eslint-disable-line
    try {
      let gdprComplete = true;
      const user = Meteor.users.findOne({ _id: this.userId }, { fields: { settings: 1 } });
      const gdprSettings = user.settings.filter(setting => setting.isGDPR === true);
      gdprSettings.forEach(({ lastUpdatedByUser }) => {
        if (!lastUpdatedByUser) gdprComplete = false;
      });
      return gdprComplete;
    } catch (exception) {
      handleMethodException(exception);
    }
  },
  'users.saveGDPRSettings': function usersSaveGDPRSettings() {
    // eslint-disable-line
    try {
      const user = Meteor.users.findOne({ _id: this.userId }, { fields: { _id: 1, settings: 1 } });
      user.settings = user.settings
        .filter(setting => setting.isGDPR === true)
        .map(gdprSetting => ({
          ...gdprSetting,
          lastUpdatedByUser: new Date().toISOString()
        }));
      return Meteor.users.update({ _id: user._id }, { $set: { settings: user.settings } });
    } catch (exception) {
      handleMethodException(exception);
    }
  },
  'users.updateSetting': function usersUpdateSetting(setting) {
    // eslint-disable-line
    check(setting, Object);

    try {
      const user = Meteor.users.findOne({ _id: setting.userId || this.userId });
      const settingToUpdate = user.settings.find(({ _id }) => _id === setting._id);

      if (setting.userId && Roles.userIsInRole(this.userId, 'admin')) {
        settingToUpdate.value = setting.value;
        settingToUpdate.lastUpdatedByAdmin = new Date().toISOString();
      }

      if (!setting.userId && user._id === this.userId) {
        settingToUpdate.value = setting.value;
        settingToUpdate.lastUpdatedByUser = new Date().toISOString();
      }

      return Meteor.users.update(
        { _id: setting.userId || this.userId },
        { $set: { settings: user.settings } }
      );
    } catch (exception) {
      handleMethodException(exception);
    }
  }
});
rateLimit({
  methods: [
    'users.editProfile',
    'users.changeStorageLimit',
    'users.changeUsedStorage',
    'users.sendUpgradeRequestEmail',
    'users.changeRole',
    'users.softDelete',
    'users.restore',
    'users.hardDelete',
    'users.insert',
    'users.setPassword',
    'users.update',
    'users.sendVerificationEmail',
    'users.sendWelcomeEmail',
    'users.fetchSettings',
    'users.checkIfGDPRComplete',
    'users.saveGDPRSettings',
    'users.updateSetting'
  ],
  limit: 5,
  timeRange: 1000
});
